import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Table, Space, Button, Modal, Row, DatePicker, Col, Tag, Tabs } from "antd";
import { Switch, Route, useHistory } from "react-router-dom";

// React-Redux
import { useDispatch } from "react-redux";

// Icons
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";

// Firebase
import firebase from "firebase/compat/app";

// Lodash & Moment
import moment from "moment";

import DetailsComponent from "./Details";
import ChangeStatusDropdown from "./ChangeStatusDropdown";
import { newOpId, stamp } from "../../../modules/helpers/audit";
import Search from "antd/es/input/Search";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

function sorterFn(property) {
  return (a, b) => `${a[property]}`.toLowerCase().localeCompare(`${b[property]}`.toLowerCase());
}

const { confirm } = Modal;

// In-memory cache for companies (shared across component instances)
let companiesCache = null;
let companiesCacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export default function MessagesListComponent() {
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingUrgentTable, setUrgentLoadingTable] = useState(true);
  const [columns, setColumns] = useState([]);
  const [messages, setMessages] = useState([]);
  const [urgentMessages, setUrgentMessages] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [urgentStartDate] = useState(moment().subtract(7, "days"));
  const [search, setSearch] = useState("");
  const [searchRange, setSearchRange] = useState([moment().subtract(1, "months"), moment()]);
  // Tab keys: "simple_unique", "simple_multiple", "recommande_unique", "recommande_multiple"
  const [activeTab, setActiveTab] = useState("simple_unique");
  // Ligne surlignée en bleu : celle dont l'opérateur vient d'ouvrir le détail (onglet séparé)
  const [highlightedKey, setHighlightedKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const dispatch = useDispatch();
  const history = useHistory();

  const db = useRef(firebase.firestore()).current;
  const unsubscribeUrgentRef = useRef(null);
  const unsubscribeMessagesRef = useRef(null);
  const unsubscribeRecipientsRef = useRef(null);
  const unsubscribeCompaniesRef = useRef(null);

  // Companies cache with Map for O(1) lookups
  const companiesMap = useMemo(() => {
    const map = new Map();
    companies.forEach((c) => map.set(c.id, c));
    return map;
  }, [companies]);

  // Load companies with caching
  useEffect(() => {
    // Check if cache is valid
    if (
      companiesCache &&
      companiesCacheTimestamp &&
      Date.now() - companiesCacheTimestamp < CACHE_TTL
    ) {
      setCompanies(companiesCache);
      return;
    }

    // Fetch companies only once with real-time updates
    unsubscribeCompaniesRef.current = db.collection("companies").onSnapshot((snapshot) => {
      const companiesList = snapshot.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }));

      // Update cache
      companiesCache = companiesList;
      companiesCacheTimestamp = Date.now();

      setCompanies(companiesList);
    });

    return () => {
      if (unsubscribeCompaniesRef.current) {
        unsubscribeCompaniesRef.current();
      }
    };
  }, [db]);

  // Fetch urgent messages with real-time updates
  const fetchUrgentMessages = useCallback(() => {
    if (companies.length === 0) return;

    setUrgentLoadingTable(true);

    // Clean up previous listener
    if (unsubscribeUrgentRef.current) {
      unsubscribeUrgentRef.current();
    }

    // Real-time listener for urgent recipients (both simple and recommande)
    unsubscribeUrgentRef.current = db
      .collectionGroup("recipients")
      .where("send_type", "in", ["simple", "recommande"])
      .where("status", "==", "prepare")
      .where("created_at", "<=", urgentStartDate.toDate())
      .onSnapshot(async (recipientsSnapshot) => {
        if (!recipientsSnapshot.docs.length) {
          setUrgentMessages([]);
          setUrgentLoadingTable(false);
          return;
        }

        // Group recipients by message ID
        const recipientsObj = {};
        const messageIds = new Set();

        recipientsSnapshot.docs.forEach((recipientDoc) => {
          const recipient = { ...recipientDoc.data(), key: recipientDoc.id };
          const msgId = recipient.message.id;
          messageIds.add(msgId);

          if (recipientsObj[msgId]) {
            recipientsObj[msgId].push(recipient);
          } else {
            recipientsObj[msgId] = [recipient];
          }
        });

        // Fetch messages in parallel (limit to 10 concurrent to avoid overwhelming Firestore)
        const messageRefs = [...messageIds].map(
          (msgId) =>
            recipientsSnapshot.docs.find((d) => d.data().message.id === msgId).ref.parent.parent
        );

        const uniqueRefs = [...new Map(messageRefs.map((ref) => [ref.path, ref])).values()];

        const messagePromises = uniqueRefs.map((ref) => ref.get());
        const messageSnapshots = await Promise.all(messagePromises);

        const results = messageSnapshots.map((message) => {
          const companyId = message.ref.parent.parent.id;
          return {
            ...message.data(),
            key: message.id,
            companyId,
            company: {
              ...companiesMap.get(companyId),
              id: companyId,
            },
            recipients: recipientsObj[message.id] || [],
          };
        });

        setUrgentMessages(results);
        setUrgentLoadingTable(false);
      });
  }, [db, urgentStartDate, companiesMap, companies.length]);

  // Fetch regular messages with real-time updates and optimized queries
  const fetchMessages = useCallback(() => {
    if (companies.length === 0) return;

    setLoadingTable(true);

    // Clean up previous listeners
    if (unsubscribeRecipientsRef.current) {
      unsubscribeRecipientsRef.current();
    }
    if (unsubscribeMessagesRef.current) {
      unsubscribeMessagesRef.current();
    }

    const recipientsMap = new Map();
    const messageIdsSet = new Set();

    // Real-time listener for recipients (both simple and recommande)
    unsubscribeRecipientsRef.current = db
      .collectionGroup("recipients")
      .where("send_type", "in", ["simple", "recommande"])
      .where("created_at", ">=", searchRange[0].toDate())
      .where("created_at", "<=", searchRange[1].toDate())
      .onSnapshot((recipientsSnapshot) => {
        // Clear maps
        recipientsMap.clear();
        messageIdsSet.clear();

        if (!recipientsSnapshot.docs.length) {
          setMessages([]);
          setLoadingTable(false);
          return;
        }

        recipientsSnapshot.docs.forEach((recipientDoc) => {
          const recipient = { ...recipientDoc.data(), key: recipientDoc.id };
          const msgId = recipient.message.id;
          messageIdsSet.add(msgId);

          if (recipientsMap.has(msgId)) {
            recipientsMap.get(msgId).push(recipient);
          } else {
            recipientsMap.set(msgId, [recipient]);
          }
        });

        // Now listen to messages
        if (unsubscribeMessagesRef.current) {
          unsubscribeMessagesRef.current();
        }

        unsubscribeMessagesRef.current = db
          .collectionGroup("messages")
          .where("type", "in", ["simple", "recommande"])
          .where("created_at", ">=", searchRange[0].toDate())
          .where("created_at", "<=", searchRange[1].toDate())
          .onSnapshot((messagesSnapshot) => {
            const results = messagesSnapshot.docs
              .filter((message) => messageIdsSet.has(message.id)) // Only include messages with recipients
              .map((message) => {
                const companyId = message.ref.parent.parent.id;
                return {
                  ...message.data(),
                  key: message.id,
                  companyId,
                  company: {
                    ...companiesMap.get(companyId),
                    id: companyId,
                  },
                  recipients: recipientsMap.get(message.id) || [],
                };
              });

            setMessages(results);
            setLoadingTable(false);
          });
      });
  }, [db, searchRange, companiesMap, companies.length]);

  const handleMarkAsSent = useCallback(
    (record) => {
      // Même garde que ChangeStatusDropdown : un envoi papier marqué "Envoyé" sans
      // pages/addresses_count fausse la facturation (l'export compta lit ces champs).
      const isPaperSend = record.type === "simple" || record.type === "recommande";
      const hasPrintingFields = record.pages > 0 && record.addresses_count > 0;
      if (isPaperSend && !hasPrintingFields) {
        Modal.warning({
          title: "Champs obligatoires non renseignés",
          icon: <ExclamationCircleOutlined />,
          width: "650px",
          content: (
            <div>
              <p>Avant de passer au statut "Envoyé", veuillez renseigner :</p>
              <ul>
                {(!record.pages || record.pages <= 0) && <li>Le nombre de pages</li>}
                {(!record.addresses_count || record.addresses_count <= 0) && (
                  <li>Le nombre d'adresses</li>
                )}
              </ul>
              <p>Ces champs se trouvent dans la section "Impression (Opérateur)".</p>
            </div>
          ),
          okText: "Compris",
        });
        return;
      }
      confirm({
        title: "Marquer comme envoyé",
        icon: <ExclamationCircleOutlined />,
        content: <div>Êtes-vous sûr de vouloir marquer l'envoi comme étant "Envoyé" ?</div>,
        okText: "Oui",
        okType: "primary",
        onOk: () => {
          return db
            .collection("companies")
            .doc(record.companyId)
            .collection("messages")
            .doc(record.key)
            .set(
              {
                status: "sent",
                status_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
                last_change: stamp({ op: newOpId() }),
              },
              { merge: true }
            );
        },
      });
    },
    [db]
  );

  const handleOpenDetails = useCallback((record) => {
    // Surligne la ligne en bleu : au retour de l'onglet, l'opérateur retrouve sa position
    setHighlightedKey(record.key);
    window.open(`/admin/sent/${record.companyId}/${record.key}`, "_blank");
  }, []);

  // Memoized columns to prevent recreation
  const tableColumns = useMemo(
    () => [
      {
        title: "Date de dépôt",
        dataIndex: "date",
        sorter: sorterFn("date"),
        render: (date) =>
          date ? moment(date.toDate()).tz("Europe/Paris").format("DD/MM/YYYY HH:mm") : "",
        sortOrder: "descend",
      },
      {
        title: "Date d'envoi",
        dataIndex: "recipients",
        render: (recipients) =>
          recipients && recipients[0] && recipients[0].send_date
            ? moment(recipients[0].send_date.toDate()).tz("Europe/Paris").format("DD/MM/YYYY")
            : "",
        sortOrder: "descend",
      },
      {
        title: "Nom du compte client",
        dataIndex: "company",
        render: (company) => company?.name || "",
        sortOrder: "descend",
      },
      {
        title: "Type",
        dataIndex: "type",
        render: (t, o) => (t === "simple" ? "Lettre simple" : `Recommandé ${o.level}`),
      },
      {
        title: "Destinataires",
        dataIndex: "recipients_type",
        render: (recipientsType) => (recipientsType === "multiple" ? "Multiples" : "Unique"),
      },
      {
        title: "Avec accusé de réception",
        dataIndex: "receipt_aknowledge",
        render: (receipt, o) => (
          <div>{o.type === "simple" ? "-" : receipt === "yes" ? "Oui" : "Non"}</div>
        ),
      },
      {
        title: "Pièces jointes",
        dataIndex: "attachments",
        render: (attachment) => <div>{attachment?.length || 0}</div>,
      },
      {
        title: "Status",
        key: "status",
        filters: [
          { text: "En préparation", value: "prepare" },
          { text: "Envoyé", value: "sent" },
          { text: "Erreurs multiples", value: "error_multiple" },
          { text: "Erreur d'adressage", value: "error_address" },
          { text: "Erreur fichier", value: "error_file" },
          { text: "Erreur d'impression", value: "error_print" },
          { text: "Erreur paramètres d'impression", value: "error_print_param" },
          { text: "Annulé", value: "cancelled" },
        ],
        onFilter: (value, record) => {
          const status = record.status || (record.recipients && record.recipients[0]?.status);
          return status === value;
        },
        render: (_, record) => {
          if (!record.recipients || !record.recipients[0]) {
            return null;
          }
          return {
            props: {
              style: { padding: 0, backgroundColor: "#fafafa" },
            },
            children: <ChangeStatusDropdown record={record} style={{ height: "100%" }} />,
          };
        },
      },
      {
        title: "Détails",
        key: "action",
        render: (text, record) => (
          <Space size="small">
            {record && (record.status || record.recipients?.[0]?.status) !== "sent" && (record.status || record.recipients?.[0]?.status) !== "cancelled" && (
              <Button
                type="primary"
                title="Marquer comme envoyer"
                size="small"
                shape="circle"
                icon={<CheckCircleOutlined />}
                onClick={() => handleMarkAsSent(record)}
              />
            )}
            <Button
              onClick={() => handleOpenDetails(record)}
              type="primary"
              title="Details"
              size="small"
              shape="circle"
              icon={<InfoCircleOutlined />}
            />
          </Space>
        ),
      },
    ],
    [handleMarkAsSent, handleOpenDetails]
  );

  // Initialize
  useEffect(() => {
    dispatch({ type: "CLEAR_MESSAGES_HISTORY" });
    setColumns(tableColumns);
  }, [dispatch, tableColumns]);

  // Fetch data when companies are loaded
  useEffect(() => {
    if (companies.length > 0) {
      fetchUrgentMessages();
      fetchMessages();
    }
  }, [companies.length, fetchUrgentMessages, fetchMessages]);

  // Re-fetch when search range changes
  useEffect(() => {
    if (companies.length > 0) {
      fetchMessages();
    }
  }, [searchRange, companies.length, fetchMessages]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeUrgentRef.current) {
        unsubscribeUrgentRef.current();
      }
      if (unsubscribeMessagesRef.current) {
        unsubscribeMessagesRef.current();
      }
      if (unsubscribeRecipientsRef.current) {
        unsubscribeRecipientsRef.current();
      }
      if (unsubscribeCompaniesRef.current) {
        unsubscribeCompaniesRef.current();
      }
    };
  }, []);

  // Helper to check if message matches tab criteria
  const matchesTab = useCallback((m, tab) => {
    const [msgType, recipientType] = tab.split("_");
    const isSimple = msgType === "simple";
    const isUnique = recipientType === "unique";

    // Check message type
    if (m.type !== msgType) return false;

    // Check recipients type
    if (isSimple) {
      // For "simple", use recipients_type field
      return isUnique ? m.recipients_type === "unique" : m.recipients_type === "multiple";
    } else {
      // For "recommande", use recipients array length
      return isUnique ? m.recipients?.length === 1 : m.recipients?.length > 1;
    }
  }, []);

  const filteredMessages = useMemo(() => {
    let filtered = messages.filter((m) => matchesTab(m, activeTab));
    if (search) {
      // Insensible à la casse : nom du compte client OU N° d'envoi (ID, affiché en majuscules).
      const searchLower = search.trim().toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.company?.name?.toLowerCase().includes(searchLower) ||
          (m.key && String(m.key).toLowerCase().includes(searchLower))
      );
    }
    return filtered;
  }, [messages, search, activeTab, matchesTab]);

  const filteredUrgentMessages = useMemo(() => {
    return urgentMessages.filter((m) => matchesTab(m, activeTab));
  }, [urgentMessages, activeTab, matchesTab]);

  // Count messages with status "prepare" for each tab
  const messageCounts = useMemo(() => {
    const isPrepare = (m) => m.recipients?.[0]?.status === "prepare";
    return {
      simple_unique: messages.filter((m) => matchesTab(m, "simple_unique") && isPrepare(m)).length,
      simple_multiple: messages.filter((m) => matchesTab(m, "simple_multiple") && isPrepare(m)).length,
      recommande_unique: messages.filter((m) => matchesTab(m, "recommande_unique") && isPrepare(m)).length,
      recommande_multiple: messages.filter((m) => matchesTab(m, "recommande_multiple") && isPrepare(m)).length,
    };
  }, [messages, matchesTab]);

  const disabledDate = useCallback((current) => {
    return current && current > moment().endOf("day");
  }, []);

  return (
    <>
      <Switch>
        <Route path="/admin/sent/:company/:id">
          <DetailsComponent />
        </Route>
        <Route>
          {filteredUrgentMessages.length > 0 && (
            <>
              <h1>Envois prioritaires</h1>
              <Row>
                <Tag icon={<WarningOutlined />} color="error">
                  Ces envois sont en attente depuis plus de 7 jours
                </Tag>
              </Row>
              <Table
                bordered
                style={{ marginTop: 16 }}
                columns={tableColumns}
                dataSource={filteredUrgentMessages}
                size="small"
                scroll={filteredUrgentMessages.length ? { x: true } : undefined}
                loading={loadingUrgentTable}
                rowClassName={(record) => (record.key === highlightedKey ? "row-highlight-blue" : "")}
                pagination={{
                  pageSize: 10,
                  position: ["bottomCenter"],
                }}
              />
            </>
          )}

          <h1>Tous les envois papier</h1>
          <Row>
            <Col span={8}>
              <Search
                style={{ maxWidth: "30rem" }}
                placeholder="Recherche par compte client ou N° d'envoi"
                onSearch={setSearch}
                enterButton
              />
            </Col>
            <Col
              span={8}
              offset={8}
              style={{
                textAlign: "right",
              }}
            >
              <Space>
                <div>Filter par date de dépôt</div>
                <RangePicker
                  value={searchRange}
                  disabledDate={disabledDate}
                  onChange={setSearchRange}
                />
              </Space>
            </Col>
          </Row>

          <Tabs
            activeKey={activeTab}
            onChange={(value) => {
              setActiveTab(value);
              setCurrentPage(1); // Reset pagination to first page
            }}
          >
            <TabPane tab={`Lettres (simples) (${messageCounts.simple_unique})`} key="simple_unique" />
            <TabPane tab={`Lettres (multiples) (${messageCounts.simple_multiple})`} key="simple_multiple" />
            <TabPane tab={`Recommandées (simples) (${messageCounts.recommande_unique})`} key="recommande_unique" />
            <TabPane tab={`Recommandés (multiples) (${messageCounts.recommande_multiple})`} key="recommande_multiple" />
          </Tabs>

          <Table
            bordered
            style={{ marginTop: 16 }}
            columns={tableColumns}
            dataSource={filteredMessages}
            size="small"
            scroll={filteredMessages.length ? { x: true } : undefined}
            loading={loadingTable}
            rowClassName={(record) => (record.key === highlightedKey ? "row-highlight-blue" : "")}
            pagination={{
              current: currentPage,
              pageSize: 30,
              position: ["bottomCenter"],
              onChange: (page) => setCurrentPage(page),
            }}
          />
        </Route>
      </Switch>
    </>
  );
}
